/**
 * M10 P5a3a (plan 2026-10-17-m10-p5a §3.3): the shipyard compare panel. The flown ship and up to two shop rows side
 * by side (`models/compare.ts` holds every rule); a shop column's numbers read green when better than the flown
 * ship's and amber when worse (`data-delta`). Opened by the shop row's Compare button (`Shipyard` `onCompare`); each
 * picked column has a Remove button and the panel a Close button. Thin.
 */
import type { CSSProperties, ReactNode } from 'react'
import { FocusButton } from '../focus'
import { COMPARE_TEXT, type CompareDelta, type CompareModel } from './models/compare'
import '../strategic/strategic.css'
import './stationPanels.css'

export interface CompareProps {
  model: CompareModel
  /** Drops a picked hull (`toggleCompare`). */
  onRemove: (hull: string) => void
  /** Closes the panel (clears every pick). */
  onClose: () => void
}

const DELTA_STYLE: Readonly<Record<'better' | 'worse', CSSProperties>> = {
  better: { color: 'var(--u-good)' },
  worse: { color: 'var(--u-warn)' },
}

function deltaStyle(d: CompareDelta): CSSProperties | undefined {
  return d === 'better' || d === 'worse' ? DELTA_STYLE[d] : undefined
}

export function Compare(p: CompareProps): ReactNode {
  const m = p.model
  const picks = m.columns.filter((c) => !c.flown)
  return (
    <div className="sui fp-panel" data-testid="fleet-compare">
      <header className="sui-head">
        <h2 className="sui-title">{m.title}</h2>
      </header>
      <table className="ui-diff" data-testid="compare-table">
        <thead>
          <tr>
            <th />
            {m.columns.map((c) => (
              <th key={c.hull} data-testid={`compare-col-${c.hull}`}>
                {c.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {m.stats.map((s) => (
            <tr key={s.key}>
              <th scope="row">{s.label}</th>
              {m.columns.map((c) => {
                const cell = c.cells[s.key]
                return (
                  <td key={c.hull} className="ui-num" data-testid={`compare-${s.key}-${c.hull}`} {...(cell.delta === null ? {} : { 'data-delta': cell.delta })} style={deltaStyle(cell.delta)}>
                    {cell.text}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="fp-row__actions">
        {picks.map((c, i) => (
            <FocusButton key={c.hull} group="compare" index={i} className="ui-btn--small" testId={`compare-remove-${c.hull}`} onActivate={() => p.onRemove(c.hull)}>
              {`${COMPARE_TEXT.remove} ${c.name}`}
            </FocusButton>
          ))}
        <FocusButton group="compare" index={picks.length} className="ui-btn--small" testId="compare-close" onActivate={p.onClose}>
          {COMPARE_TEXT.close}
        </FocusButton>
      </div>
    </div>
  )
}
